import { effect, inject, Injectable, Signal, signal } from '@angular/core';

import {
  LocationsWMSFilterModel,
  WarehouseModel,
} from '@kanzi-apes/kanzi-models';
import {
  injectInfiniteQuery,
  injectQuery,
  injectQueryClient,
  keepPreviousData,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import {
  LocationsTypesQueryKeys,
  UrlPresignedKeys,
  WarehouseManagementPositionsQueryKeys,
  WarehouseManagementQueryKeys,
  WarehouseManagementZonesQueryKeys,
} from './query-key.factory';
import { KanziLocationsService } from '../services/kanzi-locations.service';
import { Store } from '@ngrx/store';
import { getWarehouseRootSelected } from '../+state/locations.selectors';
import { getUrlPresignedFilter } from '../models/locations-wms/locations.interface';

@Injectable({
  providedIn: 'root',
})
export class WarehouseWMSQueries {
  private kanziLocationsService = inject(KanziLocationsService);
  private queryClient = injectQueryClient();
  private page = signal(1);
  private locationCodeName = signal<string>('warehouse');
  private positionCodeName = signal<string>('position');
  private zoneCodeName = signal<string>('zone');
  private countryCodeName = signal<string>('country');
  private storeLocations = inject(Store);

  private warehouseRootSelected = this.storeLocations.selectSignal(
    getWarehouseRootSelected
  );
  private warehouseListFilters = signal<LocationsWMSFilterModel>({
    type__code__eq: this.locationCodeName(),
  });
  private warehousePositionsListFilters = signal<LocationsWMSFilterModel>({
    type__code__eq: this.positionCodeName(),
    warehouse_id__eq: this.warehouseRootSelected()?.id ?? null,
    limit: 50,
    offset: 0,
  });
  private warehouseZoneListFilters = signal<LocationsWMSFilterModel>({
    type__code__eq: this.zoneCodeName(),
    warehouse_id__eq: this.warehouseRootSelected()?.id ?? null,
    limit: 50,
    offset: 0,
  });

  private ZoneListFilters = signal<LocationsWMSFilterModel>({
    type__code__eq: this.zoneCodeName(),
    warehouse_id__eq: this.warehouseRootSelected()?.id ?? null,
    use_types__contains_in:
      'STORAGE,BUFFER_STORAGE,CONTROL,LOSS_CONTROL,DESTRUCTION,DONATION',
    limit: 100,
    offset: 0,
  });

  private countryListFilters = signal<LocationsWMSFilterModel>({
    type__code__eq: this.countryCodeName(),
  });

  private preDispatchLocations = signal<LocationsWMSFilterModel>({
    type__code__eq: 'position',
    warehouse_id__eq: this.warehouseRootSelected()?.id ?? null,
    use_types__contains: 'SWAP_DISPATCH',
  });

  private dispatchLocations = signal<LocationsWMSFilterModel>({
    type__code__eq: 'position',
    warehouse_id__eq: this.warehouseRootSelected()?.id ?? null,
    use_types__contains: 'DISPATCH',
  });

  public warehouseListQuery = injectInfiniteQuery(() => ({
    queryKey: WarehouseManagementQueryKeys.all(this.warehouseListFilters()),
    queryFn: async ({ pageParam }) => {
      const response = await lastValueFrom(
        this.kanziLocationsService.getWarehouseList({
          ...this.warehouseListFilters(),
          last_evaluated_key_id: pageParam,
        })
      );
      return response;
    },
    retry: 2,
    staleTime: 50000,
    placeholderData: keepPreviousData,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.last_evaluated_key ?? undefined;
    },
    enabled: !!this.warehouseListFilters()?.type__code__eq,

    select: (data) => {
      const aggregatedResults = data.pages.reduce<{
        last_evaluated_key: string | null;
        count: number;
        results: WarehouseModel[];
        warnings: string[];
      }>(
        (acc, page) => {
          acc.last_evaluated_key = page.last_evaluated_key;
          acc.count = acc.count + page.count;
          acc.results = acc.results.concat(page.results);

          return {
            last_evaluated_key: acc.last_evaluated_key,
            count: acc.count,
            results: acc.results.map((warehouse) => {
              return {
                ...warehouse,
                name_city: warehouse.parent?.name,
                address: warehouse.properties?.address,
              };
            }),
            warnings: [],
          };
        },
        {
          last_evaluated_key: null,
          count: 0,
          results: [],
          warnings: [],
        }
      );

      aggregatedResults.results.sort((a, b) => {
        return new Date(b.created).getTime() - new Date(a.created).getTime();
      });

      return aggregatedResults;
    },
  }));

  public locationTypeListQuery = injectQuery(() => ({
    queryKey: LocationsTypesQueryKeys.all(),
    queryFn: () => lastValueFrom(this.kanziLocationsService.getLocationTypes()),
    retry: 2,
    staleTime: 50000,
    placeholderData: keepPreviousData,
  }));

  /**
   * Creates a location type list query within the caller's injection context.
   * Use this in components with OnPush change detection to ensure
   * proper reactivity when the query resolves.
   * @returns A TanStack query result for the location type list
   */
  public locationTypeListComponentQuery = () =>
    injectQuery(() => ({
      queryKey: LocationsTypesQueryKeys.all(),
      queryFn: () => lastValueFrom(this.kanziLocationsService.getLocationTypes()),
      retry: 2,
      staleTime: 50000,
      placeholderData: keepPreviousData,
    }));

  public zonesListQuery = injectQuery(() => ({
    queryKey: WarehouseManagementQueryKeys.all(this.ZoneListFilters()),
    queryFn: async () => {
      const allResults: WarehouseModel[] = [];
      let offset = 0;
      const limit = 100;
      let hasNext = true;

      while (hasNext) {
        const response = await lastValueFrom(
          this.kanziLocationsService.getZonesV3({
            ...this.ZoneListFilters(),
            limit,
            offset,
          })
        );
        allResults.push(...response.results);
        hasNext = response.pagination.has_next;
        offset += limit;
      }

      return {
        count: allResults.length,
        last_evaluated_key: null,
        warnings: [] as string[],
        results: allResults,
      };
    },
    retry: 2,
    staleTime: 50000,
    placeholderData: keepPreviousData,
  }));

  public getLocationByParent = (
    filters: Signal<LocationsWMSFilterModel | null>
  ) =>
    injectQuery(() => ({
      queryKey: WarehouseManagementQueryKeys.all(
        filters() as LocationsWMSFilterModel
      ),
      queryFn: async () => {
        const allResults: WarehouseModel[] = [];
        let offset = 0;
        const limit = 100;
        let hasNext = true;

        while (hasNext) {
          const response = await lastValueFrom(
            this.kanziLocationsService.getLocationByParentV3({
              ...(filters() as LocationsWMSFilterModel),
              limit,
              offset,
            })
          );
          allResults.push(...response.results);
          hasNext = response.pagination.has_next;
          offset += limit;
        }

        return {
          count: allResults.length,
          last_evaluated_key: null,
          warnings: [] as string[],
          results: allResults,
        };
      },
      enabled: filters()?.parent_id__in !== null,
      retry: 2,
    }));

  public lossControlLocationsQuery = (
    filters: Signal<LocationsWMSFilterModel | null>
  ) =>
    injectInfiniteQuery(() => ({
      queryKey: WarehouseManagementQueryKeys.all(
        filters() as LocationsWMSFilterModel
      ),
      queryFn: async ({ pageParam }) => {
        const response = await lastValueFrom(
          this.kanziLocationsService.getWarehouseListV3({
            ...(filters() as LocationsWMSFilterModel),
            limit: 100,
            offset: pageParam,
          })
        );
        return response;
      },
      enabled: filters()?.warehouse_id__eq !== undefined,
      retry: 2,
      staleTime: 50000,
      placeholderData: keepPreviousData,
      initialPageParam: 0,
      getNextPageParam: (lastPage) =>
        lastPage.pagination.has_next
          ? lastPage.pagination.offset + lastPage.pagination.limit
          : undefined,
      select: (data) => {
        const allResults = data.pages.reduce<WarehouseModel[]>(
          (acc, page) => acc.concat(page.results),
          []
        );
        return allResults;
      },
    }));

  public getWarehouseById = (filters: Signal<LocationsWMSFilterModel | null>) =>
    injectQuery(() => ({
      queryKey: WarehouseManagementQueryKeys.all(
        filters() as LocationsWMSFilterModel
      ),
      queryFn: () =>
        lastValueFrom(
          this.kanziLocationsService.getWarehouseById(
            filters() as LocationsWMSFilterModel
          )
        ),
      enabled: !!filters()?.id__eq,
      retry: 2,
    }));

  public getUrlPresigned = (filters: Signal<getUrlPresignedFilter | null>) =>
    injectQuery(() => ({
      queryKey: UrlPresignedKeys.all(filters() as getUrlPresignedFilter),
      queryFn: () =>
        lastValueFrom(
          this.kanziLocationsService.getURLPresigned(
            filters() as getUrlPresignedFilter
          )
        ),
      enabled: filters()?.name !== null && filters()?.name !== undefined,
      retry: 2,
    }));

  public getZonePositionById = (
    filters: Signal<LocationsWMSFilterModel | null>
  ) =>
    injectQuery(() => ({
      queryKey: WarehouseManagementQueryKeys.all(
        filters() as LocationsWMSFilterModel
      ),
      queryFn: () =>
        lastValueFrom(
          this.kanziLocationsService.getZonePositionById(
            filters() as LocationsWMSFilterModel
          )
        ),
      enabled: !!filters()?.id__eq,
      retry: 2,
    }));

  public positionPreDispatchListQuery = injectInfiniteQuery(() => ({
    queryKey: WarehouseManagementPositionsQueryKeys.enlistment(
      this.preDispatchLocations()
    ),
    queryFn: async ({ pageParam }) => {
      const response = await lastValueFrom(
        this.kanziLocationsService.getWarehouseList({
          ...this.preDispatchLocations(),
          last_evaluated_key_id: pageParam || null,
        })
      );
      return response;
    },
    retry: 2,
    staleTime: 50000,
    placeholderData: keepPreviousData,
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.last_evaluated_key ?? undefined,
    enabled: !!this.preDispatchLocations()?.warehouse_id__eq,
    select: (data) => {
      const aggregatedResults = data.pages.reduce<{
        last_evaluated_key: string | null;
        count: number;
        results: WarehouseModel[];
        warnings: string[];
      }>(
        (acc, page) => {
          acc.last_evaluated_key = page.last_evaluated_key;
          acc.count = acc.count + page.count;
          acc.results = acc.results.concat(page.results);
          return acc;
        },
        {
          last_evaluated_key: null,
          count: 0,
          results: [],
          warnings: [],
        }
      );

      // Deduplicate results by id
      const seen = new Set<string>();
      aggregatedResults.results = aggregatedResults.results.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
      aggregatedResults.count = aggregatedResults.results.length;

      return aggregatedResults;
    },
  }));

  public positionDispatchListQuery = injectInfiniteQuery(() => ({
    queryKey: WarehouseManagementPositionsQueryKeys.dispatch(
      this.dispatchLocations()
    ),
    queryFn: async ({ pageParam }) => {
      const response = await lastValueFrom(
        this.kanziLocationsService.getWarehouseList({
          ...this.dispatchLocations(),
          last_evaluated_key_id: pageParam || null,
        })
      );
      return response;
    },
    retry: 2,
    staleTime: 50000,
    placeholderData: keepPreviousData,
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.last_evaluated_key ?? undefined,
    enabled: !!this.dispatchLocations()?.warehouse_id__eq,
    select: (data) => {
      const aggregatedResults = data.pages.reduce<{
        last_evaluated_key: string | null;
        count: number;
        results: WarehouseModel[];
        warnings: string[];
      }>(
        (acc, page) => {
          acc.last_evaluated_key = page.last_evaluated_key;
          acc.count = acc.count + page.count;
          acc.results = acc.results.concat(page.results);
          return acc;
        },
        {
          last_evaluated_key: null,
          count: 0,
          results: [],
          warnings: [],
        }
      );

      // Deduplicate results by id
      const seen = new Set<string>();
      aggregatedResults.results = aggregatedResults.results.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
      aggregatedResults.count = aggregatedResults.results.length;

      return aggregatedResults;
    },
  }));

  public warehousePositionListQuery = injectQuery(() => ({
    queryKey: WarehouseManagementPositionsQueryKeys.all(
      this.warehousePositionsListFilters()
    ),
    queryFn: () =>
      lastValueFrom(
        this.kanziLocationsService.getPositionsV3(
          this.warehousePositionsListFilters()
        )
      ),
    retry: 2,
    staleTime: 50000,
    placeholderData: keepPreviousData,
    enabled: !!this.warehousePositionsListFilters()?.warehouse_id__eq,
    select: (data) => {
      const offset = this.warehousePositionsListFilters().offset ?? 0;
      return {
        count: data.pagination.total,
        last_evaluated_key: null,
        warnings: data.warnings,
        results: data.results.map((position, index) => ({
          ...position,
          consecutive: String(offset + index + 1),
        })),
      };
    },
  }));

  public warehouseZoneListQuery = injectQuery(() => ({
    queryKey: WarehouseManagementZonesQueryKeys.all(
      this.warehouseZoneListFilters()
    ),
    queryFn: () =>
      lastValueFrom(
        this.kanziLocationsService.getZonesV3(
          this.warehouseZoneListFilters()
        )
      ),
    retry: 2,
    staleTime: 50000,
    placeholderData: keepPreviousData,
    select: (data) => {
      const offset = this.warehouseZoneListFilters().offset ?? 0;
      return {
        count: data.pagination.total,
        last_evaluated_key: null,
        warnings: data.warnings,
        results: data.results.map((zone, index) => ({
          ...zone,
          consecutive: String(offset + index + 1),
        })),
      };
    },
  }));

  public countryListQuery = injectQuery(() => ({
    queryKey: WarehouseManagementQueryKeys.all(this.countryListFilters()),
    queryFn: () =>
      lastValueFrom(
        this.kanziLocationsService.getWarehouseListV3({
          ...this.countryListFilters(),
          limit: 100,
          offset: 0,
        })
      ),
    retry: 2,
    staleTime: 50000,
    placeholderData: keepPreviousData,
  }));

  /**
   * Creates a country list query within the caller's injection context.
   * Use this in components with OnPush change detection to ensure
   * proper reactivity when the query resolves.
   * @returns A TanStack query result for the country list
   */
  public countryListComponentQuery = () =>
    injectQuery(() => ({
      queryKey: WarehouseManagementQueryKeys.all(this.countryListFilters()),
      queryFn: () =>
        lastValueFrom(
          this.kanziLocationsService.getWarehouseListV3({
            ...this.countryListFilters(),
            limit: 100,
            offset: 0,
          })
        ),
      retry: 2,
      staleTime: 50000,
      placeholderData: keepPreviousData,
    }));

  /**
   * Creates a parent list query (departments/cities) within the caller's injection context.
   * Uses the V3 locations endpoint with offset/limit pagination.
   * @param filters - Signal containing the filter with parent_id__eq
   * @returns A TanStack query result for the child locations
   */
  public parentListQuery = (filters: Signal<LocationsWMSFilterModel | null>) =>
    injectQuery(() => ({
      queryKey: WarehouseManagementQueryKeys.all(
        filters() as LocationsWMSFilterModel
      ),
      queryFn: () =>
        lastValueFrom(
          this.kanziLocationsService.getWarehouseListV3({
            ...(filters() as LocationsWMSFilterModel),
            limit: 100,
            offset: 0,
          })
        ),
      enabled: filters()?.parent_id__eq !== undefined,
      retry: 2,
      select: (data) => data.results,
    }));

  public updateWarehouseWMSFilters(newFilters: LocationsWMSFilterModel) {
    this.warehouseListFilters.update((currentFilter) => {
      const mergedFilters = Object.keys({
        ...currentFilter,
        ...newFilters,
      }).reduce((acc, key) => {
        const typedKey = key as keyof LocationsWMSFilterModel;
        if (newFilters[typedKey] !== '' && currentFilter[typedKey] !== '') {
          acc[typedKey] = newFilters[typedKey] ?? currentFilter[typedKey];
        }
        return acc;
      }, {} as LocationsWMSFilterModel);

      return mergedFilters;
    });
  }
  public updatePositionsWMSFilters(newFilters: LocationsWMSFilterModel) {
    this.warehousePositionsListFilters.update((currentFilter) => {
      const mergedFilters = Object.keys({
        ...currentFilter,
        ...newFilters,
      }).reduce((acc, key) => {
        const typedKey = key as keyof LocationsWMSFilterModel;
        if (newFilters[typedKey] !== '' && currentFilter[typedKey] !== '') {
          acc[typedKey] = newFilters[typedKey] ?? currentFilter[typedKey];
        }
        return acc;
      }, {} as LocationsWMSFilterModel);

      return mergedFilters;
    });
  }

  public updateZoneWMSFilters(newFilters: LocationsWMSFilterModel) {
    this.warehouseZoneListFilters.update((currentFilter) => {
      const mergedFilters = Object.keys({
        ...currentFilter,
        ...newFilters,
      }).reduce((acc, key) => {
        const typedKey = key as keyof LocationsWMSFilterModel;
        if (newFilters[typedKey] !== '' && currentFilter[typedKey] !== '') {
          acc[typedKey] = newFilters[typedKey] ?? currentFilter[typedKey];
        }
        return acc;
      }, {} as LocationsWMSFilterModel);

      return mergedFilters;
    });
  }

  /**
   * Resets position filters to their default state, preserving the warehouse context.
   * Uses signal.set() to avoid the merge logic that can drop required keys.
   * @param warehouseId - The warehouse ID to preserve in the filter, or null if none selected
   */
  public resetPositionsWMSFilters(warehouseId: string | null | undefined): void {
    this.warehousePositionsListFilters.set({
      type__code__eq: this.positionCodeName(),
      warehouse_id__eq: warehouseId,
      limit: 50,
      offset: 0,
    });
  }

  /**
   * Resets zone filters to their default state, preserving the warehouse context.
   * Uses signal.set() to avoid the merge logic that can drop required keys.
   * @param warehouseId - The warehouse ID to preserve in the filter, or null if none selected
   */
  public resetZoneWMSFilters(warehouseId: string | null | undefined): void {
    this.warehouseZoneListFilters.set({
      type__code__eq: this.zoneCodeName(),
      warehouse_id__eq: warehouseId,
      limit: 50,
      offset: 0,
    });
  }

  constructor() {
    effect(
      (): void => {
        const warehouseId = this.warehouseRootSelected()?.id;

        if (warehouseId) {
          // Si `warehouseId` es válido, actualiza los filtros
          this.warehousePositionsListFilters.set({
            type__code__eq: this.positionCodeName(),
            warehouse_id__eq: warehouseId,
            limit: 50,
            offset: 0,
          });

          this.warehouseZoneListFilters.set({
            type__code__eq: this.zoneCodeName(),
            warehouse_id__eq: warehouseId,
            limit: 50,
            offset: 0,
          });

          this.ZoneListFilters.set({
            warehouse_id__eq: warehouseId,
            type__code__eq: this.zoneCodeName(),
            use_types__contains_in:
              'STORAGE,BUFFER_STORAGE,CONTROL,LOSS_CONTROL,DESTRUCTION,DONATION',
            limit: 100,
            offset: 0,
          });
          this.preDispatchLocations.set({
            warehouse_id__eq: warehouseId,
            type__code__eq: 'position',
            use_types__contains: 'SWAP_DISPATCH',
          });
          this.dispatchLocations.set({
            warehouse_id__eq: warehouseId,
            type__code__eq: 'position',
            use_types__contains: 'DISPATCH',
          });
        } else {
          // Si no hay `warehouseId`, limpia o mantiene los filtros por defecto
          this.warehousePositionsListFilters.set({
            type__code__eq: this.positionCodeName(),
            limit: 50,
            offset: 0,
          });
          this.warehouseZoneListFilters.set({
            type__code__eq: this.zoneCodeName(),
            limit: 50,
            offset: 0,
          });
          this.ZoneListFilters.set({
            type__code__eq: this.zoneCodeName(),
            use_types__contains_in:
              'STORAGE,BUFFER_STORAGE,CONTROL,LOSS_CONTROL,DESTRUCTION,DONATION',
            limit: 100,
            offset: 0,
          });
          this.preDispatchLocations.set({
            type__code__eq: 'position',
            use_types__contains: 'SWAP_DISPATCH',
          });
          this.dispatchLocations.set({
            type__code__eq: 'position',
            use_types__contains: ' DISPATCH',
          });
        }
      },
      { allowSignalWrites: true }
    );
    effect(() => {
      if (
        !this.warehouseListQuery.isPlaceholderData() &&
        this.warehouseListQuery.data()?.last_evaluated_key
      ) {
        this.queryClient.prefetchQuery({
          queryKey: WarehouseManagementQueryKeys.all({
            ...this.warehouseListFilters(),
            last_evaluated_key_id:
              this.warehouseListQuery.data()?.last_evaluated_key,
          }),
          queryFn: () =>
            lastValueFrom(
              this.kanziLocationsService.getWarehouseList({
                ...this.warehouseListFilters(),
                last_evaluated_key_id:
                  this.warehouseListQuery.data()?.last_evaluated_key,
              })
            ),
        });
      }
    });

    // Auto-fetch all pages for pre-dispatch locations
    effect(() => {
      if (
        this.positionPreDispatchListQuery.hasNextPage() &&
        !this.positionPreDispatchListQuery.isFetchingNextPage()
      ) {
        this.positionPreDispatchListQuery.fetchNextPage();
      }
    });

    // Auto-fetch all pages for dispatch locations
    effect(() => {
      if (
        this.positionDispatchListQuery.hasNextPage() &&
        !this.positionDispatchListQuery.isFetchingNextPage()
      ) {
        this.positionDispatchListQuery.fetchNextPage();
      }
    });
  }
}
